import {
  IElementAttachment,
  isIElementAttachment,
  IElementType,
} from "@faro-lotv/ielement-types";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { GUID } from "@faro-lotv/foundation";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";

interface UseRefreshAttachmentReturnValue {
  /**
   * Checks if a given attachment has an expired `signedUri`.
   */
  isAttachmentExpired: (attachment: IElementAttachment) => boolean;

  /**
   * Attempts to refresh attachments with expired `signedUri` by fetching the latest
   * versions from the backend using their IDs.
   */
  refreshAttachmentIfExpired: (ids: GUID[]) => Promise<IElementAttachment[]>;
}

/**
 * Hook to provide a reusable function to refresh attachments if their signed URI has expired.
 */
export function useRefreshAttachment(): UseRefreshAttachmentReturnValue {
  const { projectId, updateAttachments, attachments } = useMarkupContext();
  const { handleErrorWithToast } = useErrorContext();

  const projectApiClient = useProjectApiClient({ projectId });

  /**
   * Checks if an attachment's signed URI has expired.
   *
   * @param attachment The attachment to check.
   * @returns True if expired, false otherwise.
   */
  function isAttachmentExpired(attachment: IElementAttachment): boolean {
    if (!attachment.signedUriExpiresOn) {
      return false;
    }

    const expirationDate = new Date(attachment.signedUriExpiresOn);
    return new Date() > expirationDate;
  }

  /**
   * Attempts to refresh the attachments by fetching the latest version from the backend.
   *
   * This function takes a list of attachment IDs and checks if any of them are expired. For each expired attachment,
   * it fetches the latest version from the backend. If the fetch is successful, the attachment is updated with
   * the new `signedUri` and `signedUriExpiresOn`. In case of failure or invalid data,
   * the original attachment is returned unchanged.
   *
   * @param ids An array of attachment IDs to check for expiration and refresh if necessary.
   * @returns A Promise that resolves to an array of updated attachments. If no attachments are expired or cannot be fetched,
   *          it returns the original attachments list unchanged.
   */
  async function refreshAttachmentIfExpired(
    ids: GUID[]
  ): Promise<IElementAttachment[]> {
    const attachmentsToRefresh: IElementAttachment[] = [];

    try {
      const { page: attachmentsFetched } = await projectApiClient.getIElements({
        ids,
        types: [IElementType.attachment],
      });

      const fetchedMap = new Map(
        attachmentsFetched.filter(isIElementAttachment).map((attachment) => {
          attachmentsToRefresh.push(attachment);
          return [attachment.id, attachment];
        })
      );

      const updatedAttachments = attachments.map(
        (attachment) => fetchedMap.get(attachment.id) ?? attachment
      );

      updateAttachments(updatedAttachments);
    } catch (error) {
      handleErrorWithToast({
        id: `refreshAttachment-${Date.now().toString()}`,
        title: "Could not refresh the attachment",
        error,
      });

      throw Error("Error while refreshing attachment");
    }

    return attachmentsToRefresh;
  }

  return {
    refreshAttachmentIfExpired,
    isAttachmentExpired,
  };
}
